import { useEffect, useState } from 'react';
import { Node, useReactFlow } from 'reactflow';

import { timer } from 'd3-timer';

const animationDuration = 500;

const easeInOut = (ratio: number) => (Math.sin((ratio - 0.5) * Math.PI) + 1) / 2;

// Base from here:
// https://pro.reactflow.dev/examples/expand-collapse
export const useAnimatedNodes = (inputNodes: Node[]) => {
  const [animatedNodes, setAnimatedNodes] = useState(inputNodes);
  const { getNode } = useReactFlow();

  useEffect(() => {
    let hasMoved = false;
    const transitions = inputNodes.map((node) => {
      const before = getNode(node.id);
      const from = (before?.hidden ? node.position : before?.position) ?? node.position;
      const to = node.position;
      const hasNodeMoved = from.x !== to.x || from.y !== to.y;
      if (!hasMoved && hasNodeMoved) {
        hasMoved = true;
      }
      return {
        id: node.id,
        from,
        to,
        node,
      };
    });
    // No need to do the animation unless some node has moved
    if (!hasMoved) {
      setAnimatedNodes(inputNodes);
      return;
    }

    const animation = timer((elapsed) => {
      const easedFactor = easeInOut(elapsed / animationDuration);

      const nodes = transitions.map(({ node, from, to }) => {
        const position = {
          x: from.x + (to.x - from.x) * easedFactor,
          y: from.y + (to.y - from.y) * easedFactor,
        };
        return {
          ...node,
          position,
        };
      });

      setAnimatedNodes(nodes);

      if (elapsed > animationDuration) {
        // it's important to set the final nodes here to avoid glitches
        setAnimatedNodes(inputNodes);
        animation.stop();
      }
    });

    return () => animation.stop();
  }, [inputNodes, getNode]);

  return { animatedNodes };
};
